import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarRange,
  Check,
  LockKeyhole,
  Mail,
  Shield,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { getAdminSeasons, getTeamInvites, errorMessage } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useClubOnboarding } from "../hooks/useClubOnboarding";
import { useSetupRecovery, useSetupWrite } from "../hooks/useSetupWrite";
import {
  inviteSchema,
  isPendingSetupInvite,
  readSetup,
  samePendingSetupWrite,
  roleLabels,
  seasonSchema,
  slugFromName,
  staffRoles,
  steps,
  teamSchema,
  type OnboardingState,
} from "../onboardingState";
import type { AdminTeamRow, Season } from "@/lib/types";
import "../onboarding.css";

type Setup = ReturnType<typeof useClubOnboarding>;
type Write = ReturnType<typeof useSetupWrite>;

export function WelcomeShell() {
  const setup = useClubOnboarding();
  const navigate = useNavigate();
  const authRedirect = useRef(false);
  const [celebrating, setCelebrating] = useState(false);
  useEffect(() => {
    if (setup.me.isLoading || setup.error) return;
    if (!setup.me.data?.user) {
      if (!authRedirect.current) {
        authRedirect.current = true;
        void navigate({
          to: "/login",
          search: { next: "/welcome" },
          replace: true,
        });
      }
    } else if (!setup.allowed) void navigate({ to: "/hub", replace: true });
    else if (
      !setup.loading &&
      !setup.error &&
      !setup.empty &&
      !setup.resumable &&
      !setup.state.pending &&
      setup.state.step !== "done"
    )
      void navigate({ to: "/admin/overview", replace: true });
    else if (setup.state.completed && !celebrating)
      void navigate({ to: "/admin/overview", replace: true });
  }, [
    setup.me.isLoading,
    setup.me.isError,
    setup.me.data,
    setup.allowed,
    setup.loading,
    setup.error,
    setup.empty,
    setup.resumable,
    setup.state.pending,
    setup.state.completed,
    setup.state.step,
    celebrating,
    navigate,
  ]);
  if (setup.error)
    return (
      <SetupGuard
        error
        retry={() => {
          void setup.me.refetch();
          if (setup.allowed) void setup.teams.refetch();
        }}
      />
    );
  if (setup.loading) return <SetupGuard />;
  if (!setup.allowed) return null;
  if (!setup.empty && !setup.team && !setup.state.pending) return null;
  return (
    <SetupFlow
      key={setup.key}
      setup={setup}
      onFinish={() => setCelebrating(true)}
    />
  );
}

export function SetupGuard({
  error = false,
  retry,
}: {
  error?: boolean;
  retry?: () => void;
}) {
  return (
    <main className="onb-guard bg-surface rounded" aria-live="polite">
      <h1>{error ? "We couldn’t check your club." : "Checking your club…"}</h1>
      <p>
        {error
          ? "A failed request doesn’t mean your club is empty. Try again before starting setup."
          : "Confirming your access and whether a first team is needed."}
      </p>
      {error && <Button onClick={retry}>Try again</Button>}
    </main>
  );
}

function SetupFlow({
  setup,
  onFinish,
}: {
  setup: Setup;
  onFinish: () => void;
}) {
  const write = useSetupWrite(setup);
  const recovery = useSetupRecovery(setup);
  const navigate = useNavigate();
  const { state, team } = setup;
  const client = useQueryClient();
  const seasons = useQuery({
    queryKey: unscopedKey("onboarding-seasons", { key: setup.key }),
    queryFn: getAdminSeasons,
    retry: false,
  });
  const invites = useQuery({
    queryKey: unscopedKey("onboarding-invites", {
      key: setup.key,
      teamId: team?.id,
    }),
    queryFn: () => getTeamInvites(team!.id),
    enabled: !!team,
    retry: false,
  });
  const season = seasons.data?.find((s) => s.id === state.seasonId);
  useEffect(() => {
    if (!seasons.isSuccess || seasons.isFetching || !state.seasonId || season)
      return;
    // A different tab can save a season before this tab refreshes its list.
    // Only a fresh authorized read may discard the saved anchor.
    let cancelled = false;
    const anchor = state.seasonId;
    void getAdminSeasons()
      .then((rows) => {
        if (cancelled || readSetup(setup.key).seasonId !== anchor) return;
        client.setQueryData(
          unscopedKey("onboarding-seasons", { key: setup.key }),
          rows,
        );
        if (!rows.some((row) => row.id === anchor))
          setup.update((s) => ({
            ...s,
            seasonId: null,
            draft: { ...s.draft, existingSeasonId: "" },
          }));
      })
      .catch(() => {
        if (!cancelled) void seasons.refetch();
      });
    return () => {
      cancelled = true;
    };
  }, [
    seasons.isSuccess,
    seasons.isFetching,
    state.seasonId,
    season,
    setup.key,
    setup.update,
    client,
  ]);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, [state.step]);
  const busy = write.isPending || recovery.isPending;
  const later = () => {
    setup.update({ dismissed: true });
    void navigate({ to: "/admin/overview" });
  };
  const finish = () => {
    onFinish();
    setup.update({ step: "done", completed: true, dismissed: true });
  };
  const pendingInvites =
    invites.data?.filter((i) => isPendingSetupInvite(i)) ?? [];
  const visibleInvites =
    invites.data?.filter(
      (i) => i.status === "pending" || i.status === "expired",
    ) ?? [];
  const missingRequest =
    recovery.data?.status === "missing" &&
    state.pending &&
    samePendingSetupWrite(recovery.data.intent, state.pending)
      ? recovery.data.intent
      : null;
  const current = steps.indexOf(state.step);
  return (
    <>
      <div className="onb-context">
        <span>{setup.me.data?.current_club?.name}</span>
        <span>Club administrator</span>
      </div>
      <main data-testid="welcome-page" className="onb-main">
        <div className="onb-wrap">
          <header className="onb-heading">
            <div>
              <div className="eyebrow">Club admin · Getting started</div>
              <h1>A place for every team.</h1>
              <p>
                Set up {setup.me.data?.current_club?.name}, one step at a time.
              </p>
            </div>
            {state.step !== "done" && (
              <Button variant="ghost" disabled={busy} onClick={later}>
                Set up later
              </Button>
            )}
          </header>
          <ol className="onb-progress" aria-label="Setup progress">
            {[
              ["First team", "Required"],
              ["Season", "With your team"],
              ["Invite staff", "Optional"],
              ["Ready", "Your club"],
            ].map(([title, detail], index) => (
              <li
                key={title}
                aria-current={current === index ? "step" : undefined}
                className={index < current ? "is-complete" : ""}
              >
                <span>
                  {index < current ? (
                    <Check size={16} aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <div>
                  <strong>{title}</strong>
                  <small>{detail}</small>
                </div>
              </li>
            ))}
          </ol>
          <div className="onb-layout">
            <aside className="onb-guide">
              <div className="onb-guide-mark">
                <Building2 aria-hidden />
              </div>
              <div className="eyebrow">{setup.me.data?.current_club?.name}</div>
              <h2>
                Start small.
                <br />
                Build from here.
              </h2>
              <p>
                Your first team gives matches, recordings and people a place to
                belong.
              </p>
              <ul>
                <li>
                  <UsersRound aria-hidden />
                  Create one team to get started
                </li>
                <li>
                  <CalendarRange aria-hidden />
                  Your first team starts in a season
                </li>
                <li>
                  <Mail aria-hidden />
                  Invite the people who help
                </li>
              </ul>
              <div className="onb-guide-bottom">
                <Shield aria-hidden />
                <p>Club administration stays separate from coaching access.</p>
              </div>
            </aside>
            <section className="onb-panel bg-surface rounded" aria-busy={busy}>
              <h2 className="sr-only" tabIndex={-1} ref={heading}>
                {["First team", "Season", "Invite staff", "Ready"][current]}
              </h2>
              {state.pending && !write.isPending ? (
                <>
                  <Copy
                    eyebrow="Checking a previous request"
                    title="Let’s confirm what was saved."
                    text="The previous response was interrupted. We’ll check the records before continuing."
                  />
                  <ErrorText error={recovery.error} />
                  <Button
                    disabled={busy}
                    onClick={() =>
                      recovery.mutate(undefined, { onSuccess: write.reset })
                    }
                  >
                    Check saved records
                  </Button>
                  {missingRequest && (
                    <>
                      <p role="status" className="onb-hint">
                        No matching saved record was found. An interrupted
                        request may still finish later.
                        {missingRequest.kind === "team"
                          ? " You can safely retry the original team URL."
                          : " Continue without repeating this optional request, and review it later in Club admin."}
                      </p>
                      <Button
                        disabled={busy}
                        onClick={() =>
                          recovery.mutate(
                            {
                              action:
                                missingRequest.kind === "team"
                                  ? "retry-team"
                                  : "skip",
                              intent: missingRequest,
                            },
                            { onSuccess: write.reset },
                          )
                        }
                      >
                        {missingRequest.kind === "team"
                          ? "Retry first team"
                          : missingRequest.kind === "season"
                            ? "Continue without this season"
                            : "Continue without this invitation"}
                      </Button>
                    </>
                  )}
                  <div className="onb-actions">
                    <Button variant="ghost" disabled={busy} onClick={later}>
                      Open club overview
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <ErrorText error={write.error} />
                  {state.step === "team" &&
                    (team ? (
                      <>
                        <Copy
                          eyebrow="Step 1 · Required"
                          title="Your team is created."
                          text="You can update team details in Club admin after setup."
                        />
                        <TeamCard team={team} />
                        <Actions
                          busy={busy}
                          next="Continue to season"
                          onNext={() => setup.update({ step: "season" })}
                        />
                      </>
                    ) : (
                      <TeamForm setup={setup} write={write} />
                    ))}
                  {state.step === "season" &&
                    (season ? (
                      <>
                        <Copy
                          eyebrow="Step 2 · Season"
                          title="Your season is ready."
                          text={`${season.name} is available across your club’s teams.`}
                        />
                        <div className="onb-team-card">
                          <CalendarRange aria-hidden />
                          <div>
                            <strong>{season.name}</strong>
                            <p>
                              {[season.starts_on, season.ends_on]
                                .filter(Boolean)
                                .join(" – ") || "Dates not set"}
                            </p>
                          </div>
                          <span>Added</span>
                        </div>
                        <Actions
                          busy={busy}
                          back={() => setup.update({ step: "team" })}
                          next="Continue to invitations"
                          onNext={() => setup.update({ step: "invite" })}
                        />
                      </>
                    ) : (
                      <SeasonForm
                        setup={setup}
                        write={write}
                        seasons={seasons.data ?? []}
                        loading={
                          seasons.isPending ||
                          (!!state.seasonId && !season && !seasons.isError)
                        }
                        error={seasons.error}
                        retry={() => void seasons.refetch()}
                      />
                    ))}
                  {state.step === "invite" && team && (
                    <>
                      <Copy
                        eyebrow="Step 3 · Optional"
                        title="Bring your staff on board."
                        text={`Invite a coach or team admin to ${team.name}. Each invitation grants access to this team only.`}
                      />
                      <InviteForm
                        setup={setup}
                        write={write}
                        disabled={invites.isPending || invites.isError}
                      />
                      <p className="onb-hint">
                        Coaches can create feedback. Team admins manage team
                        operations. Neither role grants club administration.
                      </p>
                      {invites.isError ? (
                        <>
                          <ErrorText error={invites.error} />
                          <Button
                            variant="ghost"
                            onClick={() => void invites.refetch()}
                          >
                            Try again
                          </Button>
                        </>
                      ) : invites.isPending ? (
                        <p role="status">Checking invitations…</p>
                      ) : (
                        <div className="onb-invites">
                          <div className="eyebrow">
                            Pending invitations · {pendingInvites.length}
                          </div>
                          {visibleInvites.map((i) => (
                            <div key={i.id}>
                              <div>
                                {i.email}
                                <small>
                                  {roleLabels[
                                    i.role as keyof typeof roleLabels
                                  ] ?? i.role}
                                </small>
                              </div>
                              <span>
                                {!isPendingSetupInvite(i)
                                  ? "Expired — send a new invitation"
                                  : i.last_delivery_status === "sent"
                                    ? "Email sent"
                                    : i.last_delivery_status === "failed"
                                      ? "Delivery failed"
                                      : i.last_delivery_status === "disabled"
                                        ? "Email off"
                                        : i.last_delivery_status ===
                                            "not_configured"
                                          ? "Email not configured"
                                          : "Pending"}
                              </span>
                            </div>
                          ))}
                          {!pendingInvites.length && (
                            <p className="onb-hint">
                              No pending invitations yet.
                            </p>
                          )}
                        </div>
                      )}
                      <Actions
                        busy={busy}
                        back={() => setup.update({ step: "season" })}
                        next={
                          invites.isSuccess && !pendingInvites.length
                            ? "Finish without invitations"
                            : "Finish setup"
                        }
                        onNext={finish}
                      />
                    </>
                  )}
                  {state.step === "done" && team && (
                    <>
                      <div className="onb-complete-icon">
                        <Check aria-hidden />
                      </div>
                      <Copy
                        eyebrow="Setup complete"
                        title="A good start for your club."
                        text={`${team.name} is ready. Add players, create a match or invite more people from Club admin.`}
                      />
                      <TeamCard team={team} />
                      <dl className="onb-summary">
                        <div>
                          <dt>Team</dt>
                          <dd>{team.name}</dd>
                        </div>
                        <div>
                          <dt>Season</dt>
                          <dd>
                            {seasons.isError
                              ? "Season unavailable"
                              : seasons.isPending ||
                                  (!!state.seasonId && !season)
                                ? "Checking season…"
                                : season?.name || "Add later"}
                          </dd>
                        </div>
                        <div>
                          <dt>Staff invitations</dt>
                          <dd>
                            {invites.isError
                              ? "Invitations unavailable"
                              : invites.isPending
                                ? "Checking invitations…"
                                : pendingInvites.length
                                  ? `${pendingInvites.length} pending`
                                  : "Invite later"}
                          </dd>
                        </div>
                      </dl>
                      <div className="onb-actions">
                        <Button asChild>
                          <Link to="/admin/overview">
                            Open club overview
                            <ArrowRight aria-hidden />
                          </Link>
                        </Button>
                        <Button asChild variant="ghost">
                          <Link to="/admin/teams">Manage teams</Link>
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </main>
    </>
  );
}

function Copy({
  eyebrow,
  title,
  text,
}: {
  eyebrow: string;
  title: string;
  text: string;
}) {
  return (
    <div className="onb-copy">
      <div className="eyebrow">{eyebrow}</div>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}
function ErrorText({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="onb-error">
      {errorMessage(error)}
    </p>
  ) : null;
}
function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && (
        <p id={`${id}-error`} className="onb-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
function Actions({
  busy,
  back,
  next,
  onNext,
  skip,
  nextDisabled = false,
}: {
  busy: boolean;
  nextDisabled?: boolean;
  back?: () => void;
  next: string;
  onNext?: () => void;
  skip?: () => void;
}) {
  return (
    <div className="onb-actions">
      <div>
        {back && (
          <Button variant="ghost" disabled={busy} onClick={back}>
            <ArrowLeft aria-hidden />
            Back
          </Button>
        )}
      </div>
      <div className="onb-action-group">
        {skip && (
          <Button variant="ghost" disabled={busy} onClick={skip}>
            Do this later
          </Button>
        )}
        <Button
          type={onNext ? "button" : "submit"}
          disabled={busy || nextDisabled}
          onClick={onNext}
        >
          {busy ? "Saving…" : next}
          <ArrowRight aria-hidden />
        </Button>
      </div>
    </div>
  );
}
function TeamCard({ team }: { team: AdminTeamRow }) {
  return (
    <div className="onb-team-card">
      <div className="onb-crest">
        <Shield aria-hidden />
      </div>
      <div>
        <strong>{team.name}</strong>
        <p>
          {team.game_format} · {team.public ? "Public" : "Private"}
        </p>
      </div>
      <span className="onb-created">Created</span>
    </div>
  );
}

function useDraft<T extends "team" | "season" | "invite">(
  setup: Setup,
  kind: T,
) {
  return (value: OnboardingState["draft"][T]) =>
    setup.update((s) => ({ ...s, draft: { ...s.draft, [kind]: value } }));
}
function TeamForm({ setup, write }: { setup: Setup; write: Write }) {
  const form = useForm<OnboardingState["draft"]["team"]>({
    resolver: zodResolver(teamSchema),
    defaultValues: setup.state.draft.team,
  });
  const save = useDraft(setup, "team");
  const busy = write.isPending;
  return (
    <form
      onSubmit={form.handleSubmit((body) =>
        write.mutate({ kind: "team", body, before: [] }),
      )}
    >
      <Copy
        eyebrow="Step 1 · Required"
        title="Create your first team."
        text="Start with one team in your club’s current season. You can add the rest whenever you’re ready."
      />
      <Field
        id="setup-team-name"
        label="Team name *"
        error={form.formState.errors.name?.message}
      >
        <input
          id="setup-team-name"
          autoComplete="off"
          placeholder="e.g. Harbour U14"
          disabled={busy}
          {...form.register("name", {
            onChange: () => {
              if (!setup.state.draft.slugEdited)
                form.setValue("slug", slugFromName(form.getValues("name")));
              save(form.getValues());
            },
          })}
          aria-invalid={!!form.formState.errors.name}
        />
      </Field>
      <div className="onb-fields">
        <div>
          <Field
            id="setup-team-slug"
            label="Team URL *"
            error={form.formState.errors.slug?.message}
          >
            <input
              id="setup-team-slug"
              placeholder="harbour-u14"
              autoComplete="off"
              disabled={busy}
              {...form.register("slug", {
                onChange: () => {
                  setup.update((s) => ({
                    ...s,
                    draft: {
                      ...s.draft,
                      slugEdited: true,
                      team: form.getValues(),
                    },
                  }));
                },
              })}
              aria-invalid={!!form.formState.errors.slug}
            />
          </Field>
          <p className="onb-url">
            Team link: {window.location.host}/teams/
            {form.watch("slug") || "your-team"}
          </p>
        </div>
        <Field id="setup-team-format" label="Game format">
          <Controller
            control={form.control}
            name="game_format"
            render={({ field }) => (
              <TkticSelect
                id="setup-team-format"
                ariaLabel="Game format"
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                  save(form.getValues());
                }}
                disabled={busy}
                options={["7v7", "9v9", "11v11"].map((v) => ({
                  value: v,
                  label: v.replace("v", " v "),
                }))}
              />
            )}
          />
        </Field>
      </div>
      <div className="onb-note">
        <LockKeyhole aria-hidden />
        <div>
          <strong>Private to start.</strong>
          <p>
            Only people with access can view this team. Change visibility later
            in Teams.
          </p>
        </div>
      </div>
      <Actions busy={busy} next="Create team" />
    </form>
  );
}
function SeasonForm({
  setup,
  write,
  seasons,
  loading,
  error,
  retry,
}: {
  setup: Setup;
  write: Write;
  seasons: Season[];
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  const form = useForm<OnboardingState["draft"]["season"]>({
    resolver: zodResolver(seasonSchema),
    defaultValues: setup.state.draft.season,
  });
  const save = useDraft(setup, "season");
  const busy = write.isPending;
  const existing = setup.state.draft.seasonMode === "existing";
  return (
    <form
      onSubmit={
        existing
          ? (e) => {
              e.preventDefault();
              if (
                seasons.some(
                  (s) => s.id === setup.state.draft.existingSeasonId,
                ) &&
                !error &&
                !loading
              )
                setup.update({
                  seasonId: setup.state.draft.existingSeasonId,
                  step: "invite",
                });
            }
          : form.handleSubmit((body) =>
              write.mutate({ kind: "season", body, before: [] }),
            )
      }
    >
      <Copy
        eyebrow="Step 2 · Optional"
        title="Give the season a home."
        text="Seasons group matches and team activity. They’re shared across the club."
      />
      {loading ? (
        <p role="status">Checking club seasons…</p>
      ) : error ? (
        <>
          <ErrorText error={error} />
          <Button variant="ghost" onClick={retry}>
            Try again
          </Button>
        </>
      ) : (
        seasons.length > 0 && (
          <div className="onb-mode" role="group" aria-label="Season setup">
            {["new", "existing"].map((mode) => (
              <Button
                key={mode}
                variant={
                  setup.state.draft.seasonMode === mode ? "primary" : "ghost"
                }
                aria-pressed={setup.state.draft.seasonMode === mode}
                disabled={busy}
                onClick={() =>
                  setup.update((s) => ({
                    ...s,
                    draft: {
                      ...s.draft,
                      seasonMode: mode as "new" | "existing",
                    },
                  }))
                }
              >
                {mode === "new" ? "Create season" : "Use existing"}
              </Button>
            ))}
          </div>
        )
      )}
      {existing ? (
        <Field id="setup-existing-season" label="Club season">
          <TkticSelect
            id="setup-existing-season"
            ariaLabel="Club season"
            disabled={busy || loading || !!error}
            value={setup.state.draft.existingSeasonId}
            onChange={(v) =>
              setup.update((s) => ({
                ...s,
                draft: { ...s.draft, existingSeasonId: v },
              }))
            }
            options={seasons.map((s) => ({ value: s.id, label: s.name }))}
          />
        </Field>
      ) : (
        <>
          <Field
            id="setup-season-name"
            label="Season name *"
            error={form.formState.errors.name?.message}
          >
            <input
              id="setup-season-name"
              placeholder="e.g. Fall 2026"
              disabled={busy}
              {...form.register("name", {
                onChange: () => save(form.getValues()),
              })}
            />
          </Field>
          <div className="onb-fields">
            {(
              [
                ["starts_on", "Starts on"],
                ["ends_on", "Ends on"],
              ] as const
            ).map(([name, label]) => (
              <Field
                key={name}
                id={`setup-${name}`}
                label={label}
                error={form.formState.errors[name]?.message}
              >
                <Controller
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <TkticDateInput
                      id={`setup-${name}`}
                      value={field.value}
                      disabled={busy}
                      onChange={(v) => {
                        field.onChange(v);
                        save(form.getValues());
                      }}
                    />
                  )}
                />
              </Field>
            ))}
          </div>
          <p className="onb-hint">
            Dates are optional. You can choose the default season in Seasons
            later.
          </p>
        </>
      )}
      <Actions
        busy={busy}
        nextDisabled={
          loading ||
          !!error ||
          (existing &&
            !seasons.some((s) => s.id === setup.state.draft.existingSeasonId))
        }
        back={() => setup.update({ step: "team" })}
        skip={() => setup.update({ step: "invite" })}
        next={existing ? "Use season" : "Create season"}
      />
    </form>
  );
}
function InviteForm({
  setup,
  write,
  disabled,
}: {
  setup: Setup;
  write: Write;
  disabled: boolean;
}) {
  const form = useForm<OnboardingState["draft"]["invite"]>({
    resolver: zodResolver(inviteSchema),
    defaultValues: setup.state.draft.invite,
  });
  const save = useDraft(setup, "invite");
  useEffect(() => {
    if (!setup.state.draft.invite.email) form.setValue("email", "");
  }, [setup.state.draft.invite.email, form]);
  return (
    <form
      onSubmit={form.handleSubmit((body) => {
        if (setup.team)
          write.mutate({
            kind: "invite",
            body: {
              ...body,
              team_id: setup.team.id,
              ...(setup.state.seasonId
                ? { season_id: setup.state.seasonId }
                : {}),
            },
            before: [],
          });
      })}
    >
      <div className="onb-fields">
        <Field
          id="setup-staff-email"
          label="Staff email *"
          error={form.formState.errors.email?.message}
        >
          <input
            id="setup-staff-email"
            type="email"
            autoComplete="email"
            placeholder="coach@example.com"
            disabled={write.isPending}
            {...form.register("email", {
              onChange: () => save(form.getValues()),
            })}
          />
        </Field>
        <Field id="setup-staff-role" label="Team role">
          <Controller
            control={form.control}
            name="role"
            render={({ field }) => (
              <TkticSelect
                id="setup-staff-role"
                ariaLabel="Team role"
                disabled={write.isPending}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                  save(form.getValues());
                }}
                options={staffRoles.map((v) => ({
                  value: v,
                  label: roleLabels[v],
                }))}
              />
            )}
          />
        </Field>
      </div>
      <div className="onb-invite-action">
        <Button type="submit" disabled={write.isPending || disabled}>
          <Mail aria-hidden />
          {write.isPending ? "Sending…" : "Send invitation"}
        </Button>
      </div>
    </form>
  );
}
